import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Instagram, Youtube } from "lucide-react";

type FLink = { label: string; href: string };
type Settings = {
  brand_name: string;
  brand_tagline: string;
  shop_title: string;
  shop_links: FLink[];
  help_title: string;
  help_links: FLink[];
  newsletter_title: string;
  newsletter_text: string;
  copyright_text: string;
  instagram_url: string;
  youtube_url: string;
};

const DEFAULTS: Settings = {
  brand_name: "Jul,s Skin",
  brand_tagline: "Natural skin and hair care, crafted in small batches for healthy glow and strong hair.",
  shop_title: "Shop",
  shop_links: [
    { label: "Tops", href: "" }, { label: "Bottoms", href: "" },
    { label: "Outerwear", href: "" }, { label: "Dresses", href: "" },
  ],
  help_title: "Help",
  help_links: [
    { label: "Shipping", href: "" }, { label: "Returns", href: "" },
    { label: "Size guide", href: "" }, { label: "Contact", href: "" },
  ],
  newsletter_title: "Newsletter",
  newsletter_text: "Quiet updates, no spam.",
  copyright_text: "© {year} Jul,s Skin. All rights reserved.",
  instagram_url: "",
  youtube_url: "",
};

export function SiteFooter() {
  const [s, setS] = useState<Settings>(DEFAULTS);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("footer_settings").select("*").maybeSingle();
      if (data) setS({
        brand_name: data.brand_name,
        brand_tagline: data.brand_tagline,
        shop_title: data.shop_title,
        shop_links: (data.shop_links as FLink[]) ?? [],
        help_title: data.help_title,
        help_links: (data.help_links as FLink[]) ?? [],
        newsletter_title: data.newsletter_title,
        newsletter_text: data.newsletter_text,
        copyright_text: data.copyright_text,
        instagram_url: (data as { instagram_url?: string }).instagram_url ?? "",
        youtube_url: (data as { youtube_url?: string }).youtube_url ?? "",
      });
    })();
  }, []);

  const renderLink = (l: FLink, i: number) => {
    if (!l.href) return <li key={i}>{l.label}</li>;
    const isExternal = /^https?:\/\//i.test(l.href) || l.href.startsWith("mailto:") || l.href.startsWith("tel:");
    if (isExternal) {
      return (
        <li key={i}>
          <a href={l.href} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
            {l.label}
          </a>
        </li>
      );
    }
    const [path, qs] = l.href.split("?");
    const search: Record<string, string> = {};
    if (qs) new URLSearchParams(qs).forEach((v, k) => { search[k] = v; });
    return (
      <li key={i}>
        <Link to={path as any} search={search as any} className="hover:text-foreground transition-colors">
          {l.label}
        </Link>
      </li>
    );
  };

  return (
    <footer className="mt-32">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-12 grid gap-4 md:gap-8 grid-cols-3 text-xs md:text-sm">
        <div>
          <p className="display text-2xl">{s.brand_name}</p>
          <p className="mt-3 text-muted-foreground max-w-xs">{s.brand_tagline}</p>
          {(s.instagram_url || s.youtube_url) && (
            <div className="mt-4 flex items-center gap-2">
              {s.instagram_url && (
                <a href={s.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border hover:bg-muted transition-colors">
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {s.youtube_url && (
                <a href={s.youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border hover:bg-muted transition-colors">
                  <Youtube className="h-4 w-4" />
                </a>
              )}
            </div>
          )}
        </div>
        <div>
          <p className="eyebrow mb-3">{s.shop_title}</p>
          <ul className="space-y-2 text-muted-foreground">{s.shop_links.map(renderLink)}</ul>
        </div>
        <div>
          <p className="eyebrow mb-3">{s.help_title}</p>
          <ul className="space-y-2 text-muted-foreground">
            {s.help_links.map(renderLink)}
            <li>
              <Link to="/guides" className="hover:text-foreground transition-colors">Guides</Link>
            </li>
          </ul>
        </div>

      </div>
      <div className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        {s.copyright_text.replace("{year}", String(new Date().getFullYear()))}
      </div>
    </footer>
  );
}
